import jsPDF from 'jspdf';

const NoraPDFUploader = (feedbackJson, timerFeedback, technicalLevel) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  // Check if this is a salary negotiation report
  const isSalaryNegotiation = technicalLevel == 15;

  // Colors
  const titleColor = '#4d5e2f';
  const subtitleColor = '#6c6760';
  const textColor = '#333333';

  // Helper function to add text with word wrapping
  const addWrappedText = (text, x, y, maxWidth, fontSize = 10, color = textColor, fontWeight = 'normal') => {
    doc.setFontSize(fontSize);
    doc.setTextColor(color);
    doc.setFont('times', fontWeight);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * fontSize * 0.55); // Reduced line height multiplier
  };

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Add NoraAI Logo (text-based since we don't have the image)
  doc.setFontSize(20);
  doc.setTextColor(titleColor);
  doc.setFont('times', 'bold');
  doc.text('NoraAI', margin, yPosition);
  yPosition += 20;

  // Title - different for salary negotiation
  doc.setFontSize(22);
  doc.setTextColor(titleColor);
  doc.setFont('times', 'bold');
  const reportTitle = isSalaryNegotiation ? 'Your Salary Negotiation Report' : 'Your Mock Interview Report';
  doc.text(reportTitle, margin, yPosition);
  yPosition += 15;

  // Position and Date/Time
  doc.setFontSize(14);
  doc.setTextColor(subtitleColor);
  doc.setFont('times', 'normal');
  const positionText = feedbackJson?.position ? 
    (isSalaryNegotiation ? `${feedbackJson.position} Position` : feedbackJson.position) : 
    'Position Not Specified';
  doc.text(positionText, margin, yPosition);
  yPosition += 10;

  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };
  
  if (timerFeedback) {
    const dateString = `${timerFeedback} - ${formatDate(new Date())}`;
    doc.text(dateString, margin, yPosition);
    yPosition += 20;
  } else {
    const dateString = formatDate(new Date());
    doc.text(dateString, margin, yPosition);
    yPosition += 20;
  }

  if (isSalaryNegotiation) {
    // Salary Negotiation Report Layout
    checkNewPage(40);
    doc.setFontSize(16);
    doc.setTextColor(titleColor);
    doc.setFont('times', 'bold');
    doc.text('Overview', margin, yPosition);
    yPosition += 12;

    // Overall Performance
    if (feedbackJson?.overallPerformance) {
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Overall Performance', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.overallPerformance, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // What You Did Well
    if (feedbackJson?.whatTheyDidWell) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('What You Did Well', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.whatTheyDidWell, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Areas for Improvement
    if (feedbackJson?.needsImprovement) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Areas for Improvement', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.needsImprovement, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Red Flags (if exists and not "Null")
    if (feedbackJson?.redFlags && feedbackJson.redFlags.toLowerCase() !== "null") {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Red Flags', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.redFlags, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Question-by-Question Analysis
    if (feedbackJson?.questionByQuestionAnalysis && feedbackJson.questionByQuestionAnalysis.length > 0) {
      checkNewPage(35);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Question-by-Question Analysis', margin, yPosition);
      yPosition += 12;

      feedbackJson.questionByQuestionAnalysis.forEach((qa, index) => {
        checkNewPage(60);
        
        // Nora Statement
        yPosition = addWrappedText(`Q${index + 1}: ${qa.noraStatement}`, margin, yPosition, maxWidth, 11, textColor, 'bold');
        yPosition += 4;

        // Your Response
        yPosition = addWrappedText('Your Response:', margin, yPosition, maxWidth, 10, textColor, 'bold');
        yPosition = addWrappedText(qa.candidateResponse, margin, yPosition, maxWidth, 10, textColor, 'normal');
        yPosition += 4;

        // Analysis
        yPosition = addWrappedText('Analysis:', margin, yPosition, maxWidth, 10, textColor, 'bold');
        yPosition = addWrappedText(qa.analysis, margin, yPosition, maxWidth, 10, textColor, 'normal');
        yPosition += 4;

        // Score
        yPosition = addWrappedText(`Score: ${qa.score}/10`, margin, yPosition, maxWidth, 10, textColor, 'bold');
        yPosition += 4;

        // Better Response (if exists and not "null")
        if (qa?.betterResponse && qa.betterResponse !== "null") {
          yPosition = addWrappedText('Better Response:', margin, yPosition, maxWidth, 10, textColor, 'bold');
          yPosition = addWrappedText(qa.betterResponse, margin, yPosition, maxWidth, 10, textColor, 'normal');
        }
        yPosition += 12;
      });
    }

    // Salary Negotiation Best Practices
    checkNewPage(50);
    doc.setFontSize(13);
    doc.setTextColor(subtitleColor);
    doc.setFont('times', 'bold');
    doc.text('Salary Negotiation Best Practices', margin, yPosition);
    yPosition += 12;

    yPosition = addWrappedText('To help you succeed in real salary negotiations, here are essential strategies every candidate should master:', margin, yPosition, maxWidth, 10, textColor, 'normal');
    yPosition += 8;

    const bestPractices = [
      'Research thoroughly: Know the market rate for your role, experience level, and location before entering any negotiation.',
      'Timing matters: Wait for a job offer before discussing salary. Let them want you first.',
      'Consider the full package: Look beyond base salary to include benefits, vacation time, stock options, and professional development opportunities.',
      'Ask open-ended questions: Use phrases like "What\'s the salary range for this position?" to gather information without showing your hand.',
      'Give a range, not a number: When pressed for a number, provide a range with your target salary at the lower end.',
      'Justify your ask: Back up your requests with specific examples of your value, achievements, and market research.',
      'Be prepared to walk away: Know your minimum acceptable offer and be willing to decline if it\'s not met.',
      'Practice active listening: Pay attention to their concerns and constraints, then address them directly.',
      'Stay professional and positive: Maintain enthusiasm for the role while advocating for fair compensation.',
      'Get everything in writing: Once you reach an agreement, ask for the offer details to be documented in writing.',
      'Don\'t accept immediately: It\'s acceptable to ask for 24-48 hours to consider an offer, even if it meets your expectations.',
      'Use silence strategically: After making your case, stay quiet and let them respond first.'
    ];

    bestPractices.forEach((practice, index) => {
      checkNewPage(15);
      yPosition = addWrappedText(`• ${practice}`, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 6;
    });

  } else {
    // Regular Interview Report Layout (original code)
    checkNewPage(40);
    doc.setFontSize(16);
    doc.setTextColor(titleColor);
    doc.setFont('times', 'bold');
    doc.text('Overview', margin, yPosition);
    yPosition += 12;

    // Summary
    doc.setFontSize(13);
    doc.setTextColor(subtitleColor);
    doc.setFont('times', 'bold');
    doc.text('Summary', margin, yPosition);
    yPosition += 8;

    if (feedbackJson?.overallSummary) {
      yPosition = addWrappedText(feedbackJson.overallSummary, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Technical Skills Summary
    checkNewPage(25);
    doc.setFontSize(13);
    doc.setTextColor(subtitleColor);
    doc.setFont('times', 'bold');
    doc.text('Technical Skills', margin, yPosition);
    yPosition += 8;

    if (feedbackJson?.technicalSkillsSummary) {
      yPosition = addWrappedText(feedbackJson.technicalSkillsSummary, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Soft Skills Summary
    checkNewPage(25);
    doc.setFontSize(13);
    doc.setTextColor(subtitleColor);
    doc.setFont('times', 'bold');
    doc.text('Soft Skills', margin, yPosition);
    yPosition += 8;

    if (feedbackJson?.softSkillsSummary) {
      yPosition = addWrappedText(feedbackJson.softSkillsSummary, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 12;
    }

    // Technical Skills Detailed Section
    checkNewPage(40);
    doc.setFontSize(16);
    doc.setTextColor(titleColor);
    doc.setFont('times', 'bold');
    doc.text('Technical Skills', margin, yPosition);
    yPosition += 12;

    // Technical Summary
    if (feedbackJson?.technicalSummary) {
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Technical Summary', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.technicalSummary, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Technical Sentiment
    if (feedbackJson?.technicalSentiment) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Technical Sentiment', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.technicalSentiment, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Technical Questions
    if (feedbackJson?.question) {
      const technicalQuestions = feedbackJson.question.filter(qa => 
        qa.categorization.toLowerCase().includes('technical')
      );

      if (technicalQuestions.length > 0) {
        checkNewPage(35);
        doc.setFontSize(13);
        doc.setTextColor(subtitleColor);
        doc.setFont('times', 'bold');
        doc.text('Technical Questions We Asked', margin, yPosition);
        yPosition += 12;

        technicalQuestions.forEach((qa, index) => {
          checkNewPage(50);
          
          // Question
          yPosition = addWrappedText(`Q${index + 1}: ${qa.question}`, margin, yPosition, maxWidth, 11, textColor, 'bold');
          yPosition += 4;

          // Your Answer
          yPosition = addWrappedText('Your Answer:', margin, yPosition, maxWidth, 10, textColor, 'bold');
          yPosition = addWrappedText(qa.candidateAnswer, margin, yPosition, maxWidth, 10, textColor, 'normal');
          yPosition += 4;

          // Analysis
          yPosition = addWrappedText('Our Analysis:', margin, yPosition, maxWidth, 10, textColor, 'bold');
          yPosition = addWrappedText(qa.analysis, margin, yPosition, maxWidth, 10, textColor, 'normal');
          yPosition += 8;
        });
      }
    }

    // Interview Suggestions
    if (feedbackJson?.technicalInterviewSuggestions) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Interview Suggestions', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.technicalInterviewSuggestions, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Resume/Background Suggestions
    if (feedbackJson?.technicalResumeSuggestions) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Resume/Background Suggestions', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.technicalResumeSuggestions, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 12;
    }

    // Soft Skills Detailed Section
    checkNewPage(40);
    doc.setFontSize(16);
    doc.setTextColor(titleColor);
    doc.setFont('times', 'bold');
    doc.text('Soft Skills', margin, yPosition);
    yPosition += 12;

    // Soft Skills Questions
    if (feedbackJson?.question) {
      const softSkillQuestions = feedbackJson.question.filter(qa => 
        qa.categorization.toLowerCase().includes('soft skill')
      );

      if (softSkillQuestions.length > 0) {
        doc.setFontSize(13);
        doc.setTextColor(subtitleColor);
        doc.setFont('times', 'bold');
        doc.text('Behavioral Questions We Asked', margin, yPosition);
        yPosition += 12;

        softSkillQuestions.forEach((qa, index) => {
          checkNewPage(50);
          
          // Question
          yPosition = addWrappedText(`Q${index + 1}: ${qa.question}`, margin, yPosition, maxWidth, 11, textColor, 'bold');
          yPosition += 4;

          // Your Answer
          yPosition = addWrappedText('Your Answer:', margin, yPosition, maxWidth, 10, textColor, 'bold');
          yPosition = addWrappedText(qa.candidateAnswer, margin, yPosition, maxWidth, 10, textColor, 'normal');
          yPosition += 4;

          // Analysis
          yPosition = addWrappedText('Our Analysis:', margin, yPosition, maxWidth, 10, textColor, 'bold');
          yPosition = addWrappedText(qa.analysis, margin, yPosition, maxWidth, 10, textColor, 'normal');
          yPosition += 8;
        });
      }
    }

    // Communication Feedback
    if (feedbackJson?.communication) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Communication Feedback', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.communication, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 8;
    }

    // Time Management Feedback
    if (feedbackJson?.timeManagement) {
      checkNewPage(25);
      doc.setFontSize(13);
      doc.setTextColor(subtitleColor);
      doc.setFont('times', 'bold');
      doc.text('Time Management Feedback', margin, yPosition);
      yPosition += 8;
      yPosition = addWrappedText(feedbackJson.timeManagement, margin, yPosition, maxWidth, 10, textColor, 'normal');
      yPosition += 12;
    }

    // Next Steps
    if (feedbackJson?.nextSteps) {
      checkNewPage(35);
      doc.setFontSize(16);
      doc.setTextColor(titleColor);
      doc.setFont('times', 'bold');
      doc.text('Our Recommendations', margin, yPosition);
      yPosition += 12;
      yPosition = addWrappedText(feedbackJson.nextSteps, margin, yPosition, maxWidth, 10, textColor, 'normal');
    }
  }

  // Generate filename
  const position = (feedbackJson?.position || 'Position').replace(/[^a-z0-9]/gi, '_');
  const reportType = isSalaryNegotiation ? 'Salary_Negotiation' : 'Interview';
  const filename = `${position}_${reportType}_Report.pdf`;

  // Save the PDF
  doc.save(filename);
};

export default NoraPDFUploader;